<template>
  <div class="tabular-import-step">
    <div class="panel__header">
      <div>
        <p class="eyebrow">Tabular import</p>
        <h2>{{ sourceTitle }}</h2>
        <p>{{ sourceDescription }}</p>
      </div>
    </div>

    <div
      class="tabular-import-dropzone"
      :class="{ 'tabular-import-dropzone--active': isDragOver, 'tabular-import-dropzone--invalid': isDragInvalid }"
      @dragover.prevent="onDragOver"
      @dragenter.prevent="onDragEnter"
      @dragleave.prevent="onDragLeave"
      @drop.prevent="onDrop"
      role="button"
      tabindex="0"
      @keydown.enter.prevent="triggerFilePicker"
      @keydown.space.prevent="triggerFilePicker"
      @click="triggerFilePicker"
    >
      <input
        ref="fileInput"
        class="tabular-import-dropzone__input"
        type="file"
        :accept="acceptedFileTypes"
        @change="onFileInput"
      />

      <div class="tabular-import-dropzone__content">
        <strong>Drag and drop a CSV, TSV, or spreadsheet here</strong>
        <p>Or click to choose a file from your computer.</p>
        <p v-if="loadedFileName" class="tabular-import-dropzone__meta">
          Loaded: {{ loadedFileName }}
          <span v-if="selectedSheetName"> · {{ selectedSheetName }}</span>
        </p>
      </div>
    </div>

    <div v-if="loadError" class="validation-warning validation-warning--strong">
      {{ loadError }}
    </div>

    <div v-if="tabularFile" class="form-grid form-grid--compact">
      <label v-if="sheetOptions.length > 1" class="field">
        <span>Sheet</span>
        <select v-model="selectedSheetName" @change="analyzeSelectedSheet">
          <option v-for="sheet in sheetOptions" :key="sheet.name" :value="sheet.name">
            {{ sheet.name }}
          </option>
        </select>
      </label>

      <label class="field field--full">
        <span>Source notes</span>
        <textarea
          v-model="sourceNotes"
          rows="3"
          placeholder="Add any context worth carrying into the prep run."
        ></textarea>
      </label>
    </div>

    <div v-if="isAnalyzing" class="empty-state">
      <h3>Reviewing the file…</h3>
      <p>Looking for transaction-like rows, likely columns, and withdrawal candidates.</p>
    </div>

    <template v-else-if="analysis">
      <div class="validation-summary">
        <p><strong>File:</strong> {{ loadedFileName }}</p>
        <p>
          <strong>Detected rows:</strong> {{ summary.candidateCount }}
          &nbsp;|&nbsp;
          <strong>Ready with tx hash:</strong> {{ summary.readyCount }}
          &nbsp;|&nbsp;
          <strong>Missing tx hash:</strong> {{ summary.missingHashCount }}
          &nbsp;|&nbsp;
          <strong>With To Address:</strong> {{ summary.withToAddressCount }}
        </p>
        <p v-if="analysis.format?.label">
          <strong>Format guess:</strong> {{ analysis.format.label }}
        </p>
      </div>

      <div v-if="analysis.warnings?.length" class="validation-warning">
        <ul class="warning-list warning-list--stacked">
          <li v-for="warning in analysis.warnings" :key="warning">{{ warning }}</li>
        </ul>
      </div>

      <div v-if="!analysis.entries.length" class="empty-state">
        <h3>No candidate transactions were detected.</h3>
        <p>You can load a different file or return to intake without losing your staged work.</p>
      </div>

      <template v-else>
        <div class="tabular-import-toolbar">
          <div class="tabular-import-toolbar__left">
            <label class="checkbox-inline">
              <input
                type="checkbox"
                :checked="allReadySelected"
                @change="setAllReady($event.target.checked)"
              />
              <span>Select all ready rows ({{ readyRows.length }})</span>
            </label>

            <button type="button" class="btn btn--ghost btn--small" @click="clearSelection" :disabled="selectedReadyCount === 0">
              Clear
            </button>
          </div>

          <div class="tabular-import-toolbar__right">
            Selected {{ selectedReadyCount }} row<span v-if="selectedReadyCount !== 1">s</span>
          </div>
        </div>

        <div class="table-scroll">
          <table class="tx-table tabular-import-table">
            <thead>
              <tr>
                <th></th>
                <th>Row</th>
                <th>Transaction hash</th>
                <th>To Address</th>
                <th>Network</th>
                <th>Amount</th>
                <th>Asset</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="entry in analysis.entries" :key="entry.id">
                <td>
                  <input
                    v-model="entry._sel"
                    type="checkbox"
                    :disabled="!entry.isSelectable"
                  />
                </td>
                <td>#{{ entry.rowIndex + 1 }}</td>
                <td>
                  <code v-if="entry.txHash">{{ entry.txHash }}</code>
                  <span v-else class="muted">Not found</span>
                </td>
                <td>
                  <code v-if="entry.toAddress">{{ entry.toAddress }}</code>
                  <span v-else class="muted">Not detected</span>
                </td>
                <td>{{ entry.network || 'Unknown' }}</td>
                <td>{{ entry.amountDisplay || '—' }}</td>
                <td>{{ entry.assetSymbol || '—' }}</td>
                <td>
                  <span class="status-pill" :class="entry.isSelectable ? 'status-pill--ready' : 'status-pill--needs_review'">
                    {{ entry.isSelectable ? 'Ready to stage' : 'Needs more detail' }}
                  </span>
                  <ul v-if="entry.warnings?.length" class="warning-list">
                    <li v-for="warning in entry.warnings" :key="warning">{{ warning }}</li>
                  </ul>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
    </template>

    <div class="panel__actions">
      <button type="button" class="btn btn--primary" :disabled="!analysis || selectedReadyCount === 0" @click="emitStageImport">
        Add selected to shared validation
      </button>
      <button v-if="tabularFile" type="button" class="btn btn--ghost" @click="resetImport">
        Clear file
      </button>
      <button type="button" class="btn btn--ghost" @click="$emit('cancel')">
        Cancel
      </button>
    </div>
  </div>
</template>

<script>
import { loadTabularFile, pickDefaultSheet } from '@/services/txnImport/loadTabularFile';
import { analyzeTabularTransactionRows } from '@/services/txnImport/analyzeTabularTransactionRows';

export default {
  name: 'CasePrepTabularImportStep',
  props: {
    sourceType: {
      type: String,
      required: true
    },
    sourceTitle: {
      type: String,
      required: true
    },
    sourceDescription: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      acceptedFileTypes: '.csv,.tsv,.xlsx,.xls,text/csv,text/tab-separated-values,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      tabularFile: null,
      selectedSheetName: '',
      sourceNotes: '',
      analysis: null,
      isAnalyzing: false,
      loadError: '',
      isDragOver: false,
      isDragInvalid: false,
      dragDepth: 0
    };
  },
  computed: {
    loadedFileName() {
      return this.tabularFile?.fileName || '';
    },
    sheetOptions() {
      return Array.isArray(this.tabularFile?.sheets) ? this.tabularFile.sheets : [];
    },
    selectedSheet() {
      return this.sheetOptions.find((sheet) => sheet.name === this.selectedSheetName) || null;
    },
    summary() {
      return this.analysis?.importSummary || {
        candidateCount: 0,
        readyCount: 0,
        missingHashCount: 0,
        withToAddressCount: 0
      };
    },
    readyRows() {
      return (this.analysis?.entries || []).filter((entry) => entry.isSelectable);
    },
    selectedReadyRows() {
      return this.readyRows.filter((entry) => entry._sel);
    },
    selectedReadyCount() {
      return this.selectedReadyRows.length;
    },
    allReadySelected() {
      return this.readyRows.length > 0 && this.readyRows.every((entry) => entry._sel);
    }
  },
  methods: {
    triggerFilePicker() {
      this.$refs.fileInput?.click?.();
    },
    onDragOver() {
      this.isDragOver = true;
    },
    onDragEnter(event) {
      this.dragDepth += 1;
      this.isDragOver = true;
      this.isDragInvalid = !this.hasSupportedFile(event.dataTransfer?.files);
    },
    onDragLeave() {
      this.dragDepth = Math.max(0, this.dragDepth - 1);
      if (this.dragDepth === 0) {
        this.isDragOver = false;
        this.isDragInvalid = false;
      }
    },
    async onDrop(event) {
      this.dragDepth = 0;
      this.isDragOver = false;
      const [file] = Array.from(event.dataTransfer?.files || []);
      this.isDragInvalid = false;

      if (!file) return;
      await this.loadFile(file);
    },
    async onFileInput(event) {
      const [file] = Array.from(event.target?.files || []);
      if (!file) return;
      await this.loadFile(file);
      event.target.value = '';
    },
    hasSupportedFile(fileList) {
      const [file] = Array.from(fileList || []);
      if (!file) return true;
      return /\.(csv|tsv|xlsx|xls)$/i.test(file.name || '');
    },
    async loadFile(file) {
      this.loadError = '';
      this.analysis = null;

      try {
        this.tabularFile = await loadTabularFile(file);
        const defaultSheet = pickDefaultSheet(this.tabularFile);
        this.selectedSheetName = defaultSheet?.name || '';
        await this.analyzeSelectedSheet();
      } catch (error) {
        console.debug('CasePrepTabularImportStep load failed', error);
        this.tabularFile = null;
        this.selectedSheetName = '';
        this.loadError = error?.message || 'Could not load that file.';
      }
    },
    async analyzeSelectedSheet() {
      if (!this.selectedSheet) {
        this.analysis = null;
        return;
      }

      this.isAnalyzing = true;
      this.loadError = '';

      try {
        const analysis = analyzeTabularTransactionRows(this.selectedSheet.rows);
        this.analysis = {
          ...analysis,
          entries: (analysis.entries || []).map((entry) => ({
            ...entry,
            _sel: entry.isSelectable
          }))
        };
      } catch (error) {
        console.debug('CasePrepTabularImportStep analyze failed', error);
        this.analysis = null;
        this.loadError = error?.message || 'Could not analyze the selected sheet.';
      } finally {
        this.isAnalyzing = false;
      }
    },
    setAllReady(checked) {
      this.readyRows.forEach((entry) => {
        entry._sel = Boolean(checked);
      });
    },
    clearSelection() {
      this.readyRows.forEach((entry) => {
        entry._sel = false;
      });
    },
    emitStageImport() {
      if (!this.analysis || !this.selectedReadyRows.length) {
        this.loadError = 'Select at least one ready row before adding it to shared validation.';
        return;
      }

      this.$emit('stage-import', {
        sourceType: this.sourceType,
        sourceTitle: this.sourceTitle,
        sourceDescription: this.sourceDescription,
        fileName: this.tabularFile?.fileName || '',
        fileSize: this.tabularFile?.fileSize || 0,
        mimeType: this.tabularFile?.mimeType || '',
        extension: this.tabularFile?.extension || '',
        sheetName: this.selectedSheetName || '',
        note: this.sourceNotes,
        analysis: this.analysis,selectedEntries: this.selectedReadyRows.map((row) => {
  const entry = { ...row };
  delete entry._sel;
  return entry;
})
      });
    },
    resetImport() {
      this.tabularFile = null;
      this.selectedSheetName = '';
      this.analysis = null;
      this.loadError = '';
      this.sourceNotes = '';
    }
  }
};
</script>

<style scoped>
.tabular-import-step {
  display: grid;
  gap: 1rem;
}

.tabular-import-dropzone {
  position: relative;
  display: grid;
  place-items: center;
  min-height: 180px;
  border: 1.5px dashed rgba(37, 99, 235, 0.32);
  border-radius: 18px;
  background: rgba(248, 250, 252, 0.84);
  cursor: pointer;
  transition: border-color 0.18s ease, background 0.18s ease, transform 0.18s ease;
}

.tabular-import-dropzone:hover,
.tabular-import-dropzone:focus-within,
.tabular-import-dropzone--active {
  border-color: rgba(37, 99, 235, 0.6);
  background: rgba(239, 246, 255, 0.92);
  transform: translateY(-1px);
}

.tabular-import-dropzone--invalid {
  border-color: rgba(220, 38, 38, 0.42);
  background: rgba(254, 242, 242, 0.92);
}

.tabular-import-dropzone__input {
  display: none;
}

.tabular-import-dropzone__content {
  text-align: center;
  display: grid;
  gap: 0.35rem;
  padding: 1.5rem;
}

.tabular-import-dropzone__content p {
  margin: 0;
}

.tabular-import-dropzone__meta {
  font-size: 0.92rem;
  color: rgba(15, 23, 42, 0.68);
}

.form-grid--compact {
  gap: 1rem;
}

.checkbox-inline {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.tabular-import-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.tabular-import-toolbar__left {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.tabular-import-toolbar__right {
  color: rgba(15, 23, 42, 0.72);
  font-size: 0.94rem;
}

.tabular-import-table code {
  font-size: 0.82rem;
  word-break: break-all;
}

.warning-list--stacked {
  margin: 0;
}

@media (max-width: 840px) {
  .tabular-import-toolbar {
    align-items: flex-start;
  }
}
</style>
